/* Keeps every open copy of the app (phone, laptop, iPad, browser tab) in step with the server.
   - Live: Supabase Realtime tells this device when something changed; it then fetches only what changed. Realtime uses
     the same security rules, so nobody hears about rows they cannot read.
   - Catch-up: whenever the app is opened, comes back to the front or regains network, and while visible every 3 minutes
     (every 30 s when the live connection is down), ONE small request (sync_state) brings what changed since the newest
     change it has seen, deletion notes, timers, the entry count and a fingerprint of the team/client/project/task lists.
     The lists are downloaded only when the fingerprint changed. If the count ever differs it reloads everything itself.
   - Never freezes: changes made here (timer, entries) show at once and are saved in the background, in order, retried
     until the network is back (also after the app was closed). See lib/sync.ts.
   - Opens instantly: a copy of the data is kept on the device (cache.ts) and refreshed in the background.
   - Only recent weeks: each device keeps the entries of the last WINDOW_DAYS days (timer, Today, Entries, editing work
     from them, also offline). Older entries stay on the server; reports and "Load older entries" ask the server. */
import type {RealtimeChannel} from '@supabase/supabase-js';
import {State, me, usesAllProjects} from './state.ts';
import type {Entry, Timer} from './lib/model.ts';
import {applyOp, mergeChanges, newest, since, type Op} from './lib/sync.ts';
import {opMadeAt, type MergeNote} from './lib/merge.ts';
import * as db from './db.ts';
import {addDays, todayStr} from './lib/time.ts';

export const WINDOW_DAYS = 35;
/** the first day this device keeps (Chicago date); moves forward every day */
export const windowStart = () => addDays(todayStr(), -WINDOW_DAYS);
const inWindow = (e: Entry) => e.date >= windowStart();
function prune() { const from = windowStart(); for (const [id, e] of base) if (e.date < from) base.delete(id); }
import {readCache, writeCache, clearCache, readOutbox, writeOutbox, clearOutbox, readNotSaved, writeNotSaved} from './cache.ts';
import {addItem, removeItem, retryOp, reasonText, parkOps, unparkOps, type NotSaved} from './lib/notsaved.ts';

export const hooks = {render: () => {}, status: () => {}, toast: (_m: string) => {}, blocked: () => {},
  /** personal / company settings may have changed (live update: now = true; a catch-up: false, main.ts limits how often) */
  prefs: (_now: boolean) => {},
  /** the server confirmed a change from this device (the welcome's "First one done!") */
  saved: (_op: Op) => {},
  /** the database merged a change from this device with changes from other devices (migration 016): tell the person */
  merged: (_notes: MergeNote[], _op: Op) => {}};
export const status = {live: false, syncing: false, lastOk: 0, problem: ''};

let userId: string | null = null, generation = 0;
let base = new Map<string, Entry>(), baseTimers: Timer[] = [];   // what the server has confirmed
let entriesWm: string | null = null, delWm: string | null = null, prevStart: number | null = null;
let stamp = '', lastCatchUp = 0;
let confirmed = 0;   // goes up each time the server confirms one of this device's changes (see catchUp: stale answers)   // fingerprint of the team/client/project/task lists; when the last catch-up succeeded
let outbox: Op[] = [];
let notSaved: NotSaved[] = [];   // changes the database refused: kept until the person retries, fixes or discards them
let channel: RealtimeChannel | null = null;
let signedIn = false;   // false while the login cannot be refreshed (offline): nothing is asked of the server meanwhile
let loaded = false;     // this login's data is on screen (start() finished); only then may a refreshed login connect
let signInWaiters: (() => void)[] = [];   // a first load with no copy on this device waits for the login (start())

/** called on every login event; a refreshed login reconnects and catches up */
export function setSignedIn(v: boolean) {
  const was = signedIn; signedIn = v;
  if (v) { const w = signInWaiters; signInWaiters = []; w.forEach(f => f()); }
  if (v && !was && userId && loaded) { connect(generation); void flush(); void catchUp({structure: true}); }
  if (!v) status.problem = 'Offline';
  hooks.status();
}

export const pendingCount = () => outbox.length;
/** the login is confirmed and this person's data is loaded: the server may be asked (birthdays.ts) */
export const connected = () => signedIn && loaded && !!userId;
/* ---------- "Not saved" (owner decision 2026-10-13: never lose a time record) ---------- */
export const notSavedItems = (): NotSaved[] => notSaved;
function keepNotSaved(next: NotSaved[]) { notSaved = next; if (userId) writeNotSaved(userId, notSaved); }
/** Try again: back into the queue as it was (a running timer's change: against the timer as it is now) */
export function retryNotSaved(id: string) {
  const it = notSaved.find(x => x.id === id); if (!it) return;
  const op = retryOp(it, baseTimers);
  outbox.push(op); if (userId) writeOutbox(userId, outbox);   // queued first, then off the list: never in neither place
  keepNotSaved(removeItem(notSaved, id));
  publish(); void flush();
}
/** Edit and save: the fixed change replaces the refused one (queued the same way) */
export function replaceNotSaved(id: string, op: Op) {
  if (!op.madeAt) op = {...op, madeAt: new Date().toISOString()};   // a fix is a new change, made now
  outbox.push(op); if (userId) writeOutbox(userId, outbox);
  keepNotSaved(removeItem(notSaved, id));
  publish(); void flush();
}
/** a fix (Suggest a fix) that was itself refused before anything was queued: the item stays, with the new reason */
export function noteNotSavedReason(id: string, reason: string) { keepNotSaved(notSaved.map(x => x.id === id ? {...x, reason, at: new Date().toISOString()} : x)); publish(); }
/** Discard (after the person confirmed it) */
export function discardNotSaved(id: string) { keepNotSaved(removeItem(notSaved, id)); publish(); }

/** what the screen shows = the server's data + this device's changes that are still on their way */
function publish() {
  const m = new Map(base); let timers = baseTimers;
  for (const op of outbox) timers = applyOp(m, timers, op);
  State.entries = [...m.values()]; State.timers = timers;
  hooks.render(); hooks.status();
}
function setStructure(s: Awaited<ReturnType<typeof db.loadStructure>>) { Object.assign(State, s); State.assign = s.assign || []; }

/* ---------- starting and stopping (login / sign-out) ---------- */
interface Snapshot { meId: string; role: string; structure: Awaited<ReturnType<typeof db.loadStructure>>; entries: Entry[]; timers: Timer[]; entriesWm: string | null; delWm: string | null; prevStart: number | null; savedAt: number; stamp?: string }
/** Returns 'ready' (data on screen), 'blocked' (this login is not an active team member) or 'stale' (signed out or
    started again meanwhile: nothing to show from this call). */
export async function start(uid: string, progress: (n: number, total: number) => void, waiting?: () => void): Promise<'ready' | 'blocked' | 'stale'> {
  stop(false);
  userId = uid; const gen = ++generation;
  outbox = readOutbox(uid); notSaved = readNotSaved<NotSaved>(uid);
  // changes still waiting when this same login signed out: back in the queue (first, in their order) and sent
  const parked = unparkOps(notSaved);
  if (parked.ops.length) {
    const have = new Set(outbox.map(o => o.opId));
    outbox = [...parked.ops.filter(o => !have.has(o.opId)), ...outbox];
    writeOutbox(uid, outbox); notSaved = parked.rest;
    if (readOutbox(uid).length === outbox.length) writeNotSaved(uid, notSaved);   // queued first, then off the list: never in neither place
  }
  // Not saved timer starts / stops refused by the old rules (before 2026-10-30, e.g. "a timer was already running"): tried
  // again ONCE by themselves through the merge in time order; they stay only if they still truly cannot be saved
  const again = notSaved.filter(x => !x.waiting && !x.op.madeAt && !x.op.autoRetried && (x.op.kind === 'startTimer' || x.op.kind === 'stopTimer'))
    .sort((a, b) => Date.parse(opMadeAt(a.op) || a.at) - Date.parse(opMadeAt(b.op) || b.at));
  if (again.length) {
    const ids = new Set(again.map(x => x.id)), have = new Set(outbox.map(o => o.opId));
    outbox = [...again.filter(x => !have.has(x.id)).map(x => ({...x.op, autoRetried: true as const})), ...outbox];
    writeOutbox(uid, outbox); notSaved = notSaved.filter(x => !ids.has(x.id));
    if (readOutbox(uid).length === outbox.length) writeNotSaved(uid, notSaved);   // queued first, then off the list
  }
  const snap = await readCache<Snapshot>(uid);
  if (gen !== generation) return 'stale';
  if (snap) {   // instant: show the copy on this device, then catch up
    State.meId = snap.meId; State.role = snap.role as any; setStructure(snap.structure); stamp = snap.stamp || '';
    base = new Map(snap.entries.map(e => [e.id, e])); baseTimers = snap.timers; entriesWm = snap.entriesWm; delWm = snap.delWm; prevStart = snap.prevStart;
    prune();
    loaded = true;
    publish();
    // opened before the login is confirmed (main.ts): if that takes a while there is no usable connection — say so (the
    // status line, Reports) instead of looking as if it were still connecting
    if (!signedIn) setTimeout(() => {
      if (gen === generation && !signedIn && !status.problem) { status.problem = navigator.onLine ? 'Could not reach the server — retrying' : 'Offline'; hooks.status(); hooks.render(); }
    }, 3000);
    connect(gen);
    void catchUp({structure: true});
    void flush();
    return 'ready';
  }
  // no copy on this device yet (first use here): the server is needed, so wait for the login (it may still be on its way)
  if (!signedIn) { waiting?.(); await new Promise<void>(r => signInWaiters.push(r)); if (gen !== generation) return 'stale'; }
  const meId = await db.myMemberId(uid);
  if (gen !== generation) return 'stale';
  if (!meId) return 'blocked';
  State.meId = meId;
  await loadAll(progress);
  if (gen !== generation) return 'stale';
  loaded = true;
  connect(gen);
  void flush();
  return 'ready';
}
/** sign-out: forget this device's copy of the data. Changes not yet sent are never deleted (owner decision 2026-10-13):
    they move to this login's own Not saved list (kept across sign-out) and are queued again at this login's next sign-in. */
export function stop(forget = true) {
  generation++; loaded = false; clearTimeout(saveTimer); savePending = false;
  { const w = signInWaiters; signInWaiters = []; w.forEach(f => f()); }   // a load waiting for the login gives up (generation changed)
  if (channel) { void db.sb.removeChannel(channel); channel = null; }
  if (forget && userId) {
    if (outbox.length) {
      const kept = parkOps(notSaved, outbox);
      writeNotSaved(userId, kept);
      if (readNotSaved<NotSaved>(userId).length === kept.length) clearOutbox(userId);   // stored first, then off the queue (else the queue stays: it is per login too)
    } else clearOutbox(userId);
    void clearCache();
  }
  userId = null; base = new Map(); baseTimers = []; outbox = []; notSaved = [];   // (the device's Not saved list itself stays)
  entriesWm = delWm = null; prevStart = null; stamp = ''; lastCatchUp = 0; status.live = false;
}

/** everything from scratch (first use on a device, after a role change, or when a count check finds a difference) */
export async function loadAll(progress?: (n: number, total: number) => void) {
  const gen = generation, started = Date.now();
  const dw = await db.newestDeletion();               // before the entries: a deletion during the load is caught next time
  const st = (await db.syncState(new Date().toISOString(), new Date().toISOString(), windowStart())).stamp;   // the lists' fingerprint, before loading them
  const [structure, timers, entries] = await Promise.all([db.loadStructure(), db.loadTimers(), db.loadEntries(progress, windowStart())]);
  if (gen !== generation) return;
  setStructure(structure); stamp = st; State.role = me()?.role ?? null;
  base = new Map(entries.map(e => [e.id, e])); baseTimers = timers;
  entriesWm = newest(entries.map(e => e.updatedAt), null); delWm = dw; prevStart = started;
  status.lastOk = Date.now(); status.problem = '';
  publish(); saveSoon();
}

/* ---------- catching up ---------- */
let running: Promise<void> | null = null, again: {structure: boolean} | null = null;
export function catchUp(opts: {structure?: boolean; force?: boolean} = {}): Promise<void> {
  if (!userId || !signedIn || !loaded) return Promise.resolve();
  if (running) { again = {structure: !!opts.structure || !!again?.structure}; return running; }
  running = (async () => {
    const gen = generation, started = Date.now(), seen = confirmed;
    status.syncing = true; hooks.status();
    try {
      // one small request; the team/client/project/task lists (the big part) only when their fingerprint changed
      const eSince = since(entriesWm, prevStart), s = await db.syncState(eSince, since(delWm, prevStart), windowStart());
      if (gen !== generation) return;
      const wantStructure = s.stamp !== stamp || (!!opts.structure && !stamp) || !!opts.force;
      const [changed, structure, meId] = await Promise.all([s.more ? db.entriesChangedSince(eSince) : s.entries,
        wantStructure ? db.loadStructure() : null, wantStructure ? db.myMemberId(userId!) : undefined]);
      const dels = s.deletions, timers = s.timers;
      if (gen !== generation) return;
      const listsChanged = !!structure && s.stamp !== stamp;   // e.g. a project moved to another client: reports ask the server again
      if (structure) stamp = s.stamp;
      if (meId === null) { hooks.blocked(); return; }      // archived (or unlinked) meanwhile
      let roleChanged = false;
      if (structure) { setStructure(structure); const role = me()?.role ?? null; roleChanged = role !== State.role; State.role = role; }
      if (changed.length || dels.length || listsChanged) State.dataVersion++;
      mergeChanges(base, changed.filter(inWindow), dels);
      for (const e of changed) if (!inWindow(e)) base.delete(e.id);   // moved to an older date: no longer kept here
      prune();
      entriesWm = newest(changed.map(e => e.updatedAt), entriesWm); delWm = newest(dels.map(d => d.deleted_at), delWm); prevStart = started;
      // A change of ours confirmed while this request was on its way may be missing from the answer. Entries are safe
      // (the newer updated_at wins), timers are not: keep ours and ask again, rather than show (and stop) a stale timer.
      const stale = confirmed !== seen;
      if (stale) again = {structure: !!again?.structure}; else baseTimers = timers;
      if (roleChanged) await loadAll();                    // what this person may see has changed
      else if (!stale && !outbox.length && (opts.force || (!changed.length && !dels.length)) && s.count !== base.size) await loadAll();   // self-check: never drift
      status.lastOk = lastCatchUp = Date.now(); status.problem = '';
      publish(); saveSoon();
      hooks.prefs(false);
    } catch (e: any) {
      status.problem = navigator.onLine ? 'Could not reach the server — retrying' : 'Offline';
      console.warn('catch-up failed', e?.message || e);
    } finally {
      status.syncing = false; running = null; hooks.status();
      if (again) { const a = again; again = null; void catchUp(a); }
    }
  })();
  return running;
}

/* ---------- live updates ---------- */
let pokeTimer: ReturnType<typeof setTimeout> | undefined, pokeStructure = false;
function poke(structure: boolean) {
  pokeStructure ||= structure;
  clearTimeout(pokeTimer);
  pokeTimer = setTimeout(() => { const s = pokeStructure; pokeStructure = false; void catchUp({structure: s}); }, 300);
}
function connect(gen: number) {
  if (gen !== generation || channel || !signedIn || !userId) return;
  const pg = (event: 'INSERT' | 'UPDATE' | '*', table: string, structure: boolean) => (c: RealtimeChannel) =>
    c.on('postgres_changes' as any, {event, schema: 'public', table}, () => poke(structure));
  // One topic per login, shared by all of this person's devices. Besides database changes it carries a tiny "poke"
  // (no data) that a device sends right after saving, so the person's other devices fetch the change at once.
  let c = db.sb.channel('ot-user-' + userId, {config: {broadcast: {self: false}}});
  c = c.on('broadcast', {event: 'poke'}, () => poke(false));
  // deletions of entries arrive as entry_deletions inserts (filtered per person); timers stopping arrive as entry inserts
  for (const add of [pg('INSERT', 'time_entries', false), pg('UPDATE', 'time_entries', false), pg('INSERT', 'entry_deletions', false),
                     pg('INSERT', 'active_timers', false), pg('UPDATE', 'active_timers', false),
                     pg('INSERT', 'members', true), pg('UPDATE', 'members', true),
                     pg('*', 'clients', true), pg('*', 'projects', true), pg('*', 'tasks', true),
                     // assignments: owner/admin hear every change; others only additions (a removal reaches them at the next
                     // catch-up through the lists' fingerprint), because deletion events are not filtered per person
                     pg(usesAllProjects() ? '*' : 'INSERT', 'project_members', true)]) c = add(c);
  // settings (migration 015): the company row, and this person's own prefs row (the owner/admins could read everyone's)
  c = c.on('postgres_changes' as any, {event: '*', schema: 'public', table: 'company_settings'}, () => hooks.prefs(true));
  if (State.meId) c = c.on('postgres_changes' as any, {event: '*', schema: 'public', table: 'member_prefs', filter: 'member_id=eq.' + State.meId}, () => hooks.prefs(true));
  // this person's birthday list (migration 017; RLS: own row only), changed on another of their devices
  if (State.meId) c = c.on('postgres_changes' as any, {event: '*', schema: 'public', table: 'member_birthdays', filter: 'member_id=eq.' + State.meId}, () => hooks.prefs(true));
  channel = c.subscribe(s => {
    const was = status.live; status.live = s === 'SUBSCRIBED'; hooks.status();
    if (status.live && !was) { void catchUp(); hooks.prefs(true); }   // (re)connected: fetch whatever happened while disconnected (personal settings too: birthdays, Also running)
  });
}

/* ---------- this device's changes: shown at once, saved in the background ---------- */
/** the project's names for a change (kept with it, see Op.names) */
function namesOf(op: Op): Op['names'] {
  const pid = (op.kind === 'saveEntry' || op.kind === 'stopTimer' ? op.entry.projectId : op.kind === 'deleteEntry' ? '' : op.timer.projectId) || '';
  const pr = State.projects.find(x => x.id === pid), cl = pr ? State.clients.find(c => c.id === pr.clientId) : undefined;
  return pr ? {project: pr.name, client: cl?.name || ''} : undefined;
}
export function enqueue(op: Op) {
  if (!op.names) { const n = namesOf(op); if (n) op = {...op, names: n}; }
  if (!op.madeAt) op = {...op, madeAt: new Date().toISOString()};   // the moment it was really made (the tap): merged in that order
  outbox.push(op); if (userId) writeOutbox(userId, outbox);
  publish(); void flush();
}
let flushing = false, retryTimer: ReturnType<typeof setTimeout> | undefined, backoff = 2000;
export async function flush(): Promise<void> {
  if (flushing || !userId || !signedIn || !loaded || !outbox.length) return;
  flushing = true; clearTimeout(retryTimer);
  const gen = generation; let sent = 0;
  try {
    while (outbox.length && gen === generation) {
      const op = outbox[0];
      let res: db.SendResult;
      try { res = await db.sendOp(op); }
      catch (e: any) {
        if (gen !== generation) return;
        if (e instanceof db.Offline) {                      // no network: keep it, try again soon
          status.problem = navigator.onLine ? 'Could not reach the server — retrying' : 'Offline';
          retryTimer = setTimeout(() => void flush(), backoff); backoff = Math.min(backoff * 2, 60000);
          return;
        }
        // refused: kept in "Not saved" (stored before it leaves the queue: never in neither place), undone on screen, said why
        const raw = String(e?.message || e), uid = userId!;
        keepNotSaved(addItem(notSaved, op, raw, undefined, op.names || namesOf(op)));
        outbox.shift(); writeOutbox(uid, outbox);
        hooks.toast("Not saved: " + reasonText(raw) + " It is kept on this device under 'Not saved' (Review).");
        // a refused stop: its timer is removed from the server first, so a start queued right after it (Switched tasks, Track
        // again) does not find the old timer still there
        if (op.kind === 'stopTimer') await notedServerVersion(op, gen); else void notedServerVersion(op, gen);
        publish(); sent++; continue;
      }
      outbox.shift(); writeOutbox(userId!, outbox); sent++; confirmed++;
      try { hooks.saved(op); } catch { /* a cheer must never stop the queue */ }
      confirmedInto(op, res);                                 // confirmed: the server's version is part of the data now
      prune();                                                // (an edit to an older entry is not kept here)
      if (res.notes.length) try { hooks.merged(res.notes, op); } catch { /* telling must never stop the queue */ }
      State.dataVersion++;
      backoff = 2000; status.problem = '';
      publish();
    }
  } finally {
    flushing = false; hooks.status();
    if (sent && gen === generation) {
      void channel?.send({type: 'broadcast', event: 'poke', payload: {}});   // tell this person's other devices
      void catchUp();                                       // get the server's version (and anything that happened meanwhile)
    }
  }
}

/** a confirmed change: what the server answered goes into its data (migration 016 may have merged it with changes from
    other devices, so the answer, not this device's version, counts); the catch-up that follows fetches anything else */
function confirmedInto(op: Op, res: db.SendResult) {
  const plain = !res.notes.length && (res.status === 'saved' || res.status === 'started' || res.status === 'deleted' || res.status === 'gone' || res.status === 'same');
  if (plain) baseTimers = applyOp(base, baseTimers, op);   // exactly as made here
  for (const id of res.gone) base.delete(id);
  for (const e of res.entries) if (inWindow(e)) base.set(e.id, e); else base.delete(e.id);
  if (res.timer !== undefined) {
    const mid = op.kind === 'deleteEntry' ? op.memberId : op.kind === 'saveEntry' ? op.entry.memberId : op.kind === 'updateTimer' ? op.prev.memberId : op.timer.memberId;
    baseTimers = [...baseTimers.filter(t => t.memberId !== mid), ...(res.timer ? [res.timer] : [])];
  }
}

/** for a refused change, delete or timer change: what the server has now (shown in Review); a refused stop: its time is
    already kept, so the server's timer is removed and does not look as if it were still running */
async function notedServerVersion(op: Op, gen: number) {
  try {
    let server: NotSaved['server'];
    if ((op.kind === 'saveEntry' && !op.isNew) || op.kind === 'deleteEntry') server = await db.entryById(op.kind === 'deleteEntry' ? op.entryId : op.entry.id);
    else if (op.kind === 'updateTimer') server = (await db.loadTimers()).find(t => t.memberId === op.prev.memberId) ?? null;
    else if (op.kind === 'stopTimer') { await db.removeTimer(op.timer.memberId, op.timer.startedAt); void catchUp(); return; }
    else return;
    if (gen !== generation) return;
    keepNotSaved(notSaved.map(x => x.id === op.opId ? {...x, server} : x)); publish();
  } catch { /* offline: Review shows "not known" */ }
}

/** a refresh asked for by the person (pull down, or "Refresh"): send what waits, reconnect the live connection if it
    dropped, then fetch everything that could be stale: changes, the lists (even if their fingerprint matches) and the
    count check. Reports ask the server again too. Returns false when the server could not be reached. */
export async function refreshNow(): Promise<boolean> {
  if (!userId) return false;
  if (!signedIn) { try { await db.sb.auth.refreshSession(); } catch { /* offline */ } }
  if (!signedIn) return false;
  await flush();
  if (!status.live && channel) { void db.sb.removeChannel(channel); channel = null; connect(generation); }
  while (running) await running;
  await catchUp({force: true});
  State.dataVersion++; publish();
  return !status.problem;
}

/* ---------- keeping the copy on this device ---------- */
let saveTimer: ReturnType<typeof setTimeout> | undefined, savePending = false;
/** the copy is written 2 s after the last change; when the app goes to the background (and may be closed) at once */
function saveNow() { if (savePending) { clearTimeout(saveTimer); writeCopy(); } }
function saveSoon() {
  clearTimeout(saveTimer); savePending = true;
  saveTimer = setTimeout(writeCopy, 2000);
}
function writeCopy() {
  savePending = false;
  if (!userId || !State.meId) return;
  const {members, clients, projects, tasks, assign} = State;
  const snap: Snapshot = {meId: State.meId, role: State.role || '', structure: {members, clients, projects, tasks, assign}, entries: [...base.values()], timers: baseTimers, entriesWm, delWm, prevStart, savedAt: Date.now(), stamp};
  void writeCache(userId, snap);
}

/* ---------- when to catch up ---------- */
let installed = false;
export function installTriggers() {
  if (installed) return; installed = true;
  // a login that could not be refreshed offline: ask again (a successful refresh calls setSignedIn(true) via main.ts)
  // While offline the login library's own refresh only fails and backs off, so it is paused; when the network is back it
  // is restarted and asked every few seconds until the login is fresh again.
  let reloginTimer: ReturnType<typeof setTimeout> | undefined;
  const relogin = () => {
    clearTimeout(reloginTimer);
    if (!userId || signedIn || !navigator.onLine) return;
    void db.sb.auth.refreshSession();
    reloginTimer = setTimeout(relogin, 5000);
  };
  const wake = () => { if (document.visibilityState === 'visible' && userId) { relogin(); void flush(); void catchUp(); } };
  let lastWake = 0;
  const wakeOnce = () => { if (Date.now() - lastWake > 2000) { lastWake = Date.now(); wake(); } };
  document.addEventListener('visibilitychange', wakeOnce);
  window.addEventListener('focus', wakeOnce);
  window.addEventListener('pageshow', wakeOnce);
  window.addEventListener('online', () => {
    // the login library remembers a failed refresh for a minute and answers every new try with that failure; the failures
    // offline were only the network, so it is forgotten: the login is refreshed (and the queue sent) at once
    try { (db.sb.auth as unknown as {lastRefreshFailure: unknown}).lastRefreshFailure = null; } catch { /* another version */ }
    backoff = 2000; void db.sb.auth.startAutoRefresh(); wake();
  });
  window.addEventListener('offline', () => { void db.sb.auth.stopAutoRefresh(); status.problem = 'Offline'; hooks.status(); });
  // a safety net: with the live connection up, changes arrive through it within a second, so asking every 3 minutes is
  // plenty; without it (blocked network, reconnecting), every 30 s
  setInterval(() => {
    if (document.visibilityState !== 'visible' || !userId) return;
    relogin(); void flush();
    if (!status.live || Date.now() - lastCatchUp > 3 * 60 * 1000 - 5000) void catchUp();
  }, 30000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') saveNow(); });
  window.addEventListener('pagehide', saveNow);
  window.addEventListener('beforeunload', ev => { if (outbox.length) { ev.preventDefault(); ev.returnValue = ''; } });   // unsaved changes stay queued anyway
}
